import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { ErpType } from "src/network/User";
import styles from "./BookingTextField.module.css";
import { IAccountingsHistoryTag } from "src/stores/krediflow/InvoiceTypes";

interface IBookingTextFieldProps {
    accountingRowIndex: number;
}

const RIMO_MAX_ROW_LENGTH = 50;

const BookingTextFieldBase = (props: IBookingTextFieldProps) => {
    const { t } = useTranslation();
    const {
        invoiceStore,
        accountingAccountSearchStore,
        accountingCostcenterStore,
        authStore,
        accountingBookingTextStore
    } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const bookingTextInputRef = useRef<HTMLTextAreaElement>(null);

    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        const erpType = authStore.user?.erpType;

        if (
            accountingAccountSearchStore.triggerFocusNextInput.isActive &&
            accountingAccountSearchStore.triggerFocusNextInput.index === accountingRowIndex
        ) {
            if (erpType === ErpType.RIMO) {
                bookingTextInputRef.current?.focus();
                accountingAccountSearchStore.setTriggerFocusNextInput(false, accountingRowIndex);
            }

            if (erpType === ErpType.IT2) {
                const accountRequiredCostcenter =
                    invoiceStore.currentInvoice?.getAccountingAccount(accountingRowIndex)?.requiresCostCenter;

                if (!accountRequiredCostcenter) {
                    bookingTextInputRef.current?.focus();
                    accountingAccountSearchStore.setTriggerFocusNextInput(false, accountingRowIndex);
                }
            }
        }

        // This useEffect only listens to changes in accountingAccountSearchStore.triggerFocusNextInput
        // eslint-disable-next-line
    }, [accountingAccountSearchStore.triggerFocusNextInput]);

    useEffect(() => {
        const erpType = authStore.user?.erpType;

        if (
            erpType === ErpType.IT2 &&
            accountingCostcenterStore.triggerFocusNextInput.isActive &&
            accountingCostcenterStore.triggerFocusNextInput.index === accountingRowIndex
        ) {
            bookingTextInputRef.current?.focus();
            accountingCostcenterStore.setTriggerFocusNextInput(false, accountingRowIndex);
        }
        // This useEffect only listens to changes in accountingCostcenterStore.triggerFocusNextInput
        // eslint-disable-next-line
    }, [accountingCostcenterStore.triggerFocusNextInput]);

    useEffect(() => {
        if (bookingTextInputRef.current && !hasInitialized) {
            const value = bookingTextInputRef.current.value || "";
            const newlineCount = (value.match(/\n/g) || []).length;

            if (newlineCount >= 1) {
                bookingTextInputRef.current.rows = 2;
            } else {
                bookingTextInputRef.current.rows = 1;
            }

            setHasInitialized(true);
        }
        // eslint-disable-next-line
    }, [bookingTextInputRef.current, hasInitialized]);

    // RIMO can handle a max of 50 (RIMO_MAX_ROW_LENGTH) characters per row
    const formatBookingTextRimo = useCallback(
        (bookingTextValue: string): string => {
            let newBookingText = bookingTextValue;

            const erpType = authStore.user?.erpType;

            if (erpType === ErpType.RIMO) {
                const newlineCountInput = (bookingTextValue.match(/\n/g) || []).length;

                if (newlineCountInput === 0 && bookingTextValue.length > RIMO_MAX_ROW_LENGTH) {
                    newBookingText =
                        bookingTextValue.slice(0, RIMO_MAX_ROW_LENGTH) +
                        "\n" +
                        bookingTextValue.slice(RIMO_MAX_ROW_LENGTH);
                }

                const lines = newBookingText.split("\n");

                if (lines.length === 2 && lines[1].length > RIMO_MAX_ROW_LENGTH) {
                    lines[1] = lines[1].slice(0, RIMO_MAX_ROW_LENGTH);
                    newBookingText = lines.join("\n");
                }
            }

            return newBookingText;
        },
        [authStore.user?.erpType]
    );

    const onChangeBookingText = useCallback(
        (bookingText: string) => {
            let newBookingText = bookingText;

            const erpType = authStore.user?.erpType;

            if (erpType === ErpType.RIMO) {
                newBookingText = formatBookingTextRimo(newBookingText);
            }

            const newlineCountNewBookingTextValue = (newBookingText.match(/\n/g) || []).length;

            // Check if the input has a newline and only allow one newline
            if (bookingTextInputRef.current) {
                if (newlineCountNewBookingTextValue === 0) {
                    bookingTextInputRef.current.rows = 1;
                } else if (newlineCountNewBookingTextValue === 1) {
                    bookingTextInputRef.current.rows = 2;
                }

                // If there's more than one newline, prevent adding more
                if (newlineCountNewBookingTextValue > 1) {
                    bookingTextInputRef.current.value = newBookingText.replace(/\n/g, "").replace(/$/, "\n");
                    return;
                }
            }

            invoiceStore.updateAccountingWithBookingText(newBookingText, accountingRowIndex);
        },
        [accountingRowIndex, authStore.user?.erpType, formatBookingTextRimo, invoiceStore]
    );

    const onChangeBookingTextInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputBookingTextArea = e.target.value;

            onChangeBookingText(inputBookingTextArea);
        },
        [onChangeBookingText]
    );

    const onBlurBookingTextField = useCallback(() => {
        invoiceStore.currentInvoice?.validateAccountingsOnFieldLevel();
    }, [invoiceStore.currentInvoice]);

    const onFocusBookingTextField = useCallback(() => {
        const textarea = bookingTextInputRef.current;
        if (textarea) {
            const textLength = textarea.value.length;

            // Use a slight delay to ensure this runs after the click event
            setTimeout(() => {
                textarea.setSelectionRange(textLength, textLength);
                textarea.focus(); // Ensure the textarea retains focus
            }, 0);
        }
    }, []);

    const onClickAccountingsHistoryTag = useCallback(
        (accountingsHistoryTag: IAccountingsHistoryTag) => async () => {
            accountingBookingTextStore.setAreAllAccountingsHistoryTagsDisplayed(
                accountingsHistoryTag.creditorId,
                false
            );

            accountingBookingTextStore.setWasAccountingsHistoryTagSelected(accountingRowIndex, true);

            onChangeBookingText(accountingsHistoryTag.text);

            bookingTextInputRef.current?.focus();
        },
        [accountingBookingTextStore, onChangeBookingText, accountingRowIndex]
    );

    const onClickDisplayAllAccountTags = useCallback(
        (creditorId: string) => () => {
            accountingBookingTextStore.setAreAllAccountingsHistoryTagsDisplayed(creditorId, true);
        },
        [accountingBookingTextStore]
    );

    const creditorId = invoiceStore.currentInvoice && invoiceStore.currentInvoice.creditor?.id;

    let accountingsHistoryTags: IAccountingsHistoryTag[] = creditorId
        ? accountingBookingTextStore.displayedAccountingsHistoryTagsByCreditorId[creditorId]
        : [];

    let accountingsHistoryTagElements = accountingsHistoryTags
        ? accountingsHistoryTags.map((accountingsHistoryTag, index) => {
              return (
                  <div
                      key={index}
                      className={styles.TagButton}
                      onClick={onClickAccountingsHistoryTag(accountingsHistoryTag)}
                  >
                      {accountingsHistoryTag.text}
                  </div>
              );
          })
        : undefined;

    if (
        accountingBookingTextStore.hasMoreAccountingsHistoryTagsToDisplay(creditorId) &&
        accountingsHistoryTagElements
    ) {
        accountingsHistoryTagElements.push(
            <div
                key={"displayMoreTags"}
                className={styles.TagButton}
                onClick={onClickDisplayAllAccountTags(creditorId ?? "")}
            >
                ...
            </div>
        );
    }

    const accountingsHistoryTagsContainerElement = (
        <div className={styles.TagContainer}>{accountingsHistoryTagElements}</div>
    );

    return (
        <>
            <UI.Input
                ref={bookingTextInputRef}
                placeholder={t("screens.kredi_flow.accountings.text").toString()}
                type="text"
                as="textarea"
                autoComplete="off"
                value={invoiceStore.currentInvoice?.getAccountingBookingText(accountingRowIndex) ?? ""}
                onChange={onChangeBookingTextInput}
                onBlur={onBlurBookingTextField}
                onFocus={onFocusBookingTextField}
                className={styles.BookingTextArea}
            />
            {!accountingBookingTextStore.wasAccountingsHistoryTagSelected[accountingRowIndex]
                ? accountingsHistoryTagsContainerElement
                : undefined}
        </>
    );
};

export const BookingTextField = Sentry.withProfiler(observer(BookingTextFieldBase));
